import React, { useState } from 'react';
import { Copy, Check, FileCode2 } from 'lucide-react';

/**
 * Request Reference panel — read-only sample request (and optional sample response)
 * shown alongside each API doc. Replaces the older interactive "Try It Live" panel.
 *
 * Props:
 *   - apiSignature?: string       (e.g. "Register" — shown as a small badge)
 *   - defaultData: string         (the XML payload to display)
 *   - title?: string              (header title, default "Request Reference")
 *   - description?: string        (one-liner under the title)
 *   - exampleResponse?: string    (optional sample response XML to display)
 */
const ApiPlayground = ({
  apiSignature,
  defaultData,
  title = 'Request Reference',
  description,
  exampleResponse,
}) => {
  const [copiedReq, setCopiedReq] = useState(false);
  const [copiedRes, setCopiedRes] = useState(false);

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 1800);
    } catch {
      /* ignore */
    }
  };

  const formatXml = (xml) => {
    if (!xml) return '';
    try {
      let formatted = '';
      let indent = 0;
      xml.replace(/>\s*</g, '>\n<').split('\n').forEach((line) => {
        if (line.match(/^<\/\w/)) indent -= 1;
        formatted += '  '.repeat(Math.max(0, indent)) + line + '\n';
        if (line.match(/^<\w[^>]*[^/]>.*$/) && !line.match(/^<\w[^>]*\/>/) && !line.includes('</')) indent += 1;
      });
      return formatted.trim();
    } catch {
      return xml;
    }
  };

  return (
    <div
      className="relative rounded-xl overflow-hidden border border-[#1F4E79]/20 shadow-lg bg-white my-6"
      data-testid="api-playground"
    >
      {/* header */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 bg-[#1F4E79] text-white">
        <div className="flex items-center gap-3 min-w-0">
          <FileCode2 size={20} className="text-[#ffcba6] flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">{title}</h3>
            {description && <p className="text-xs text-white/80 mt-0.5 line-clamp-2">{description}</p>}
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center px-2 py-1 rounded bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold uppercase tracking-wider">
          Example
        </span>
      </div>

      <div className="p-5 sm:p-6 space-y-5">
        {apiSignature && (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">API Signature</span>
            <code
              className="px-2.5 py-1 rounded bg-gray-100 border border-gray-200 text-[13px] font-mono text-[#1F4E79]"
              data-testid="playground-signature"
            >
              {apiSignature}
            </code>
          </div>
        )}

        {/* Sample Request */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Sample Request</span>
            <button
              type="button"
              onClick={() => copy(formatXml(defaultData), setCopiedReq)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F4E79] hover:text-[#163a5f] transition-colors"
              data-testid="playground-copy-request"
            >
              {copiedReq ? (
                <>
                  <Check size={14} /> Copied
                </>
              ) : (
                <>
                  <Copy size={14} /> Copy Request
                </>
              )}
            </button>
          </div>
          <pre className="overflow-x-auto p-4 text-xs leading-relaxed bg-[#1e1e1e] text-gray-100 font-mono whitespace-pre rounded-md border border-gray-800">
            {formatXml(defaultData)}
          </pre>
        </div>

        {/* Optional Example Response */}
        {exampleResponse && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Example Response</span>
              <button
                type="button"
                onClick={() => copy(formatXml(exampleResponse), setCopiedRes)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F4E79] hover:text-[#163a5f] transition-colors"
                data-testid="playground-copy-response"
              >
                {copiedRes ? (
                  <>
                    <Check size={14} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} /> Copy Response
                  </>
                )}
              </button>
            </div>
            <pre className="overflow-x-auto p-4 text-xs leading-relaxed bg-[#1e1e1e] text-gray-100 font-mono whitespace-pre rounded-md border border-gray-800">
              {formatXml(exampleResponse)}
            </pre>
          </div>
        )}

        <p className="text-[12px] text-gray-500 leading-relaxed">
          Sample shown for reference. Contact our integration team to discuss sandbox access for your build.
        </p>
      </div>
    </div>
  );
};

export default ApiPlayground;
